"use client";

/**
 * @file 页眉导航组件
 * @description 网站页眉导航栏，包含徽标、页面导航链接和用户认证按钮
 * 支持响应式设计，在移动设备上显示汉堡菜单
 * 使用"use client"指令标记为客户端组件，因为需要交互和滚动状态
 */

import { useState, useEffect } from "react"; // 导入React钩子

import Link from "next/link"; // 用于内部路由导航
import Image from "next/image"; // 用于优化图片加载
import { usePathname } from "next/navigation"; // 用于获取当前路径
import { useSession, signIn, signOut } from "next-auth/react"; // 用于会话和认证操作

/**
 * 桌面端认证按钮组件
 * @description 根据用户认证状态显示不同的按钮
 * @returns {JSX.Element} 认证按钮组件
 */
function AuthButtons() {
  const { data: session, status } = useSession();

  // 如果正在加载，显示加载动画
  if (status === "loading") {
    return (
      <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse"></div>
    );
  }

  // 如果已认证，显示用户资料和退出按钮
  if (session?.user) {
    return (
      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          {/* 用户名 */}
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-2">
            {session.user.name}
          </span>

          {/* 用户头像 */}
          <div className="relative group">
            <button className="flex items-center focus:outline-none">
              <Image
                src={session.user.image || "/images/default-avatar.png"}
                alt={session.user.name || "User"}
                width={40}
                height={40}
                className="rounded-full"
              />
            </button>

            {/* 下拉菜单 */}
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10 hidden group-hover:block">
              <Link
                href="/profile"
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Profile
              </Link>
              <button
                onClick={() => signOut({ callbackUrl: "/" })}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 如果未认证，显示登录按钮
  return (
    <button
      onClick={() => signIn("google")}
      className="flex items-center bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 px-4 py-2 rounded-full text-sm font-medium transition-colors"
    >
      <Image
        src="/icons/google-logo.svg"
        alt="谷歌"
        width={18}
        height={18}
        className="mr-2"
      />
      Sign In
    </button>
  );
}

/**
 * 移动端认证按钮组件
 * @description 移动设备的认证按钮，包括菜单关闭功能
 * @param {object} props - 组件属性
 * @param {() => void} props.closeMenu - 关闭菜单的回调函数
 * @returns {JSX.Element} 移动端认证按钮组件
 */
function MobileAuthButtons({ closeMenu }: { closeMenu: () => void }) {
  const { data: session, status } = useSession();

  // 如果正在加载，显示加载动画
  if (status === "loading") {
    return (
      <div className="h-10 w-full bg-gray-200 rounded-md animate-pulse"></div>
    );
  }

  // 如果已认证，显示用户资料和退出按钮
  if (session?.user) {
    return (
      <div className="flex flex-col space-y-3">
        {/* 用户信息 */}
        <div className="flex items-center px-4 py-3">
          <Image
            src={session.user.image || "/images/default-avatar.png"}
            alt={session.user.name || "User"}
            width={40}
            height={40}
            className="rounded-full mr-3"
          />
          <span className="font-medium text-gray-700 dark:text-gray-200">
            {session.user.name}
          </span>
        </div>

        {/* 个人资料链接 */}
        <Link
          href="/profile"
          className="text-left px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
          onClick={closeMenu}
        >
          Profile
        </Link>

        {/* 退出按钮 */}
        <button
          onClick={() => {
            signOut({ callbackUrl: "/" });
            closeMenu();
          }}
          className="text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
        >
          Sign Out
        </button>
      </div>
    );
  }

  // 如果未认证，显示登录按钮
  return (
    <button
      onClick={() => signIn("google")}
      className="flex items-center justify-center w-full px-4 py-3 rounded-md bg-white text-gray-800 border border-gray-300 hover:bg-gray-100 font-medium transition-colors"
    >
      <Image
        src="/icons/google-logo.svg"
        alt="Google"
        width={18}
        height={18}
        className="mr-2"
      />
      Sign In
    </button>
  );
}

/**
 * 页眉组件
 * @description 网站的主导航栏，具有响应式设计和滚动效果
 * @returns {JSX.Element} 页眉组件
 */
export default function Header() {
  // 管理移动菜单切换状态
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // 管理页面是否已滚动，用于更改导航栏样式
  const [isScrolled, setIsScrolled] = useState(false);
  // 获取当前路径以突出显示当前页面的导航链接
  const pathname = usePathname();

  /**
   * 管理滚动效果
   * @description 页面滚动时更改导航栏背景样式
   */
  useEffect(() => {
    // 滚动事件处理程序
    const handleScroll = () => {
      // 检查滚动距离是否超过10像素
      if (window.scrollY > 10) {
        setIsScrolled(true); // 设置为已滚动状态
      } else {
        setIsScrolled(false); // 设置为未滚动状态
      }
    };

    // 添加滚动事件监听器
    window.addEventListener("scroll", handleScroll);
    // 组件卸载时移除事件监听器以避免内存泄漏
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /**
   * 导航链接配置
   * @description 定义导航栏中的所有页面链接
   */
  const navLinks = [
    { label: "Home", href: "/" },
    { label: "Pet to Human", href: "/pet-to-human" },
  ];

  return (
    // 页眉容器 - 固定位置以保持在页面顶部，z-50确保它显示在其他元素之上
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-sm" // 滚动时，使用半透明背景和模糊效果
          : "bg-transparent" // 初始使用透明背景
      }`}
    >
      {/* 容器内容区域 - 设置宽度限制并水平居中 */}
      <div className="container mx-auto px-4">
        {/* 导航栏内部布局 - 使用flex布局，导航菜单居中 */}
        <div className="flex items-center justify-center relative h-20">
          {/* 网站徽标区域 - 固定在左侧 */}
          <Link href="/" className="absolute left-4 flex items-center">
            <Image
              src="/icons/logo.svg" // 徽标图片路径
              alt="Pet2Human AI" // 屏幕阅读器和SEO的替代文本
              width={120} // 图片宽度
              height={40} // 图片高度
              className="dark:invert" // 在暗模式下反转颜色
              priority // 设置为高优先级加载，因为这是一个重要的视觉元素
            />
          </Link>

          {/* 桌面导航菜单 - 在小屏幕上隐藏，在中等及更大屏幕上显示 */}
          <nav className="hidden md:flex items-center space-x-1">
            {/* 迭代并渲染所有导航链接 */}
            {navLinks.map((link) => (
              <Link
                key={link.href} // 使用路径作为键
                href={link.href} // 链接目标地址
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  pathname === link.href // 检查是否是当前页面
                    ? "text-blue-600 dark:text-blue-400" // 当前页面链接样式
                    : "text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100/50 dark:hover:bg-gray-800/50" // 非当前页面链接样式
                }`}
              >
                {link.label} {/* 显示链接文本 */}
              </Link>
            ))}
          </nav>
          {/* 认证按钮区域 - 在桌面上显示谷歌登录按钮或用户资料，固定在右侧 */}
          <div className="hidden md:flex items-center space-x-4 absolute right-4">
            {/* 基于认证状态的条件渲染 */}
            <AuthButtons />
          </div>
          {/* 移动菜单按钮 - 汉堡菜单按钮仅在移动设备上显示，固定在右侧 */}
          <button
            className="md:hidden p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors absolute right-4"
            onClick={() => setIsMenuOpen(!isMenuOpen)} // 点击切换菜单打开/关闭状态
            aria-label={isMenuOpen ? "Close menu" : "Open menu"} // 屏幕阅读器描述按钮功能
          >
            {/* 根据菜单状态显示不同图标 */}
            {isMenuOpen ? (
              // 关闭图标 (X)
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              // 汉堡菜单图标（三条线）
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* 移动菜单容器 - 下拉菜单仅在移动设备上显示 */}
        <div
          className={`md:hidden absolute left-0 right-0 bg-white dark:bg-gray-900 shadow-lg transition-transform duration-300 ease-in-out ${
            isMenuOpen
              ? "transform translate-y-0"
              : "transform -translate-y-full" // 使用平移动画显示/隐藏菜单
          }`}
        >
          <div className="px-4 py-6 space-y-4">
            {/* 移动导航菜单 - 垂直排列的链接列表 */}
            <nav className="flex flex-col space-y-2">
              {/* 迭代并渲染所有导航链接 */}
              {navLinks.map((link) => (
                <Link
                  key={link.href} // 使用路径作为键
                  href={link.href} // 链接目标地址
                  className={`px-4 py-3 rounded-md font-medium transition-colors ${
                    pathname === link.href // 检查是否是当前页面
                      ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20" // 当前页面链接样式，带有彩色背景
                      : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800" // 非当前页面链接样式
                  }`}
                  onClick={() => setIsMenuOpen(false)} // 点击后关闭菜单
                >
                  {link.label} {/* 显示链接文本 */}
                </Link>
              ))}
            </nav>
            {/* 移动认证按钮区域 */}
            <div className="flex flex-col space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              {/* 移动认证按钮 - 条件渲染 */}
              <MobileAuthButtons closeMenu={() => setIsMenuOpen(false)} />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
